import React, { Suspense, useContext, useEffect, useState } from 'react';
import { LoginContext } from 'src/lib/LoginContext';
import { ScrollContext } from 'src/lib/ScrollContext';

import { Banner, Button, cn, Grid2, IconWarning, Modal, Typography } from '@riac/design-system';
import { SkipLink } from 'src/components/SkipLink/SkipLink';
import RIACGoldLogo from '@riac/core/assets/riac_logo_transparent_gold_full.png?h=95';
import { IconMenu } from '@riac/design-system';

import { SideMenu } from './components/SideMenu';
import { useLocation } from 'react-router-dom';
import { HeaderAccountSection } from './components/HeaderAccountSection';
import { SocketContext } from 'src/lib/SocketContext';
import { GlobalDataContext } from 'src/lib/GlobalDataContext';
import { GlobalBannerContainer } from '../GlobalBannerContainer';
import { HeaderIconButton } from './components/HeaderIconButton';

const navigationClassName = 'text-text-lighter no-underline decoration-2 hover:text-text active:text-text decoration-sc-gold-50 text-lg leading-6 p-0 px-px';
const navigationActiveClassName = 'underline text-text hover:underline';

const NavigationLink = ({ label, to, disabled, className }: { label: string, disabled?: boolean, to: string, className?: string }) => {
  const location = useLocation();
  return (
    <Button to={disabled ? '/login' : to} variant='link'
      className={cn(navigationClassName, location.pathname.startsWith(to) && navigationActiveClassName, disabled && 'opacity-50', className)}
    >
      <Typography weight='semibold'>{label}</Typography>
    </Button>
  );
};

export const Header = () => {
  const [showSideMenu, setShowSideMenu] = useState(false);
  const { isLoggedIn } = useContext(LoginContext);
  const { headerRef } = useContext(ScrollContext);
  const { showIcon, showModal, setShowModal } = useContext(SocketContext);
  const { upcomingAuctions, refreshUpcomingAuctions } = useContext(GlobalDataContext);
  const location = useLocation();

  const orderedAuctions = upcomingAuctions.sort((a, b) => {
    if(a.active && a.AuctionTypeID !== TIMED_AUCTION_TYPE_ID) {
      return -1;
    }
    if (a.AuctionTypeID !== b.AuctionTypeID) {
      return a.AuctionTypeID - b.AuctionTypeID;
    }
    return a.AuctionID - b.AuctionID;
  });

  const getAuctionButton = (auction) => {
    const buttonClass = 'p-0 whitespace-nowrap dark:text-accent dark:hover:text-accent-hover dark:active:text-accent-active';
    if (auction.active) {
      return (<>
        <div className='text-white bg-error light p-0 text-xs leading-[22px] tracking-wide px-2 py-px flex font-bold no-underline'>LIVE</div>
        <Button className={buttonClass} to={'/live'} variant={'link'}>Bid Live</Button>
      </>);
    }
    if (auction.CatalogSearchLink) {
      let message = 'View catalog';
      if (auction.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        message = 'Bid now';
      }
      return (<Button className={buttonClass} to={`/catalog/${auction.AuctionID}`} variant={'link'}>{message}</Button>);
    }
    return (<Button className={buttonClass} to={`/gun-auctions/${auction.Slug}`} variant={'link'}>More Info</Button>);
  };

  const [scrollYOffset, setScrollYOffset] = useState(0);
  const scrollOpacity = Math.min(1, scrollYOffset * (1 / 120));

  useEffect(() => {
    const handleScroll = () => {
      setScrollYOffset(window.scrollY);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [])

  // This effect is used to update the header padding when the document's padding-right changes.
  // This can happen when the scrollbar appears or disappears
  // E.G. when the user opens a picker component.
  useEffect(() => {
    const documentElement = document.documentElement;
    let currentPadding = '';

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const newPadding = documentElement.style.paddingRight;
          if (newPadding !== currentPadding && headerRef.current) {
            headerRef.current.style.paddingRight = newPadding;
            currentPadding = newPadding;
          }
        }
      }
    });

    observer.observe(documentElement, { attributes: true });

    const initialPadding = documentElement.style.paddingRight;
    if (initialPadding && headerRef.current) {
        headerRef.current.style.paddingRight = initialPadding;
        currentPadding = initialPadding;
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {/* <Grid2 className={cn('mt-0 bg-surface-dark py-1', isTransparentHeader && 'hidden')}>
        <div className={'flex flex-col w-full'}>
          <div className={'flex flex-row justify-center divide-x'}>
            {orderedAuctions.map((auction, index) => {
              return (
                <Typography className={cn(
                  'flex px-3 items-center text-left flex-row gap-2',
                  index > 0 && 'hidden lg:flex'
                )}
                  key={index}
                  size={'sm'} weight='medium' color='text-light'
                >
                  {auction.Title} {getAuctionButton(auction)}
                </Typography>
              );
            }
            )}
          </div>
        </div>
      </Grid2> */}
      <div className='z-[9999] absolute left-px top-px hidden md:block text-red-500 font-medium text-xs'>
        {VISIBLE_VERSION_HASH === 'true' && <>v{VERSION_HASH.substring(0, 6)}</> || ''}
      </div>
      <div
        ref={headerRef}
        className={cn('fixed dark top-0 z-40 w-full mt-0 bg-surface-dark print:hidden')}
        style={{
          backgroundColor: `rgb(var(--color-surface-dark)/${scrollOpacity})`,
        }}
      >
        <Grid2 className={cn(
          'border-b-thin border-b-surface-darker py-2',
          'py-7 border-b-transparent',
          scrollOpacity > 0.8 && 'border-b-surface-darker',
        )}>
          <div className={'flex flex-col w-full'}>
            <div className={'relative flex items-center'}>
              <SkipLink />
              <Button to='/' className={cn('p-0 h-16 flex flex-row gap-8 items-center')} unstyled>
                <img src={RIACGoldLogo} alt='Rock Island Auction Company' className={cn('h-16 w-[112px]')}/>
              </Button>
              {showIcon
                && <Button aria-label='Alert' unstyled className={'text-white bg-error p-2 ml-5'} onPress={() => setShowModal(true)}>
                  <IconWarning/>
                </Button>
              }
              {/* Uncomment this to see your current break point in the header
              <>
                <span className={'inline sm:hidden'}>xs</span>
                <span className={'hidden sm:inline md:hidden'}>sm</span>
                <span className={'hidden md:inline lg:hidden'}>md</span>
                <span className={'hidden lg:inline xl:hidden'}>lg</span>
                <span className={'hidden xl:inline 2xl:hidden'}>xl</span>
                <span className={'hidden 2xl:inline'}>2xl</span>
              </>
              */}
              <div className='flex-row gap-5 hidden xl:flex absolute left-1/2 -translate-x-1/2 min-w-max'>
                <NavigationLink to='/gun-auctions' label='Auctions' />
                <NavigationLink to='/consignment' label='Consignment' />
                <NavigationLink to='/news-and-events' label='Latest' />
                <NavigationLink to='/faq' label='FAQ' />
                <NavigationLink to='/about-us' label='About us' />
                {isLoggedIn && <NavigationLink to='/account' label='Account'/>}
              </div>
              <div className='flex flex-grow' />
              <div className='justify-end self-center h-min flex-row flex gap-5 items-center'>
                <div className='flex flex-row gap-3 items-center'>
                  {isLoggedIn &&
                    <>
                      <HeaderAccountSection />
                      <HeaderIconButton
                        label='Menu'
                        IconComponent={IconMenu}
                        onPress={() => {
                          setShowSideMenu(true);
                        }}
                      />
                    </>
                  || <div className='flex flex-row gap-5'>
                    <Button to='/login' className='hidden md:flex'>
                      <Typography weight='semibold'>Log In</Typography>
                    </Button>
                    <Button to='/create-account' variant='secondary' className='hidden md:flex'>
                      <Typography weight='semibold'>Create Account</Typography>
                    </Button>
                    <HeaderIconButton
                      label='Menu'
                      IconComponent={IconMenu}
                      onPress={() => {
                        setShowSideMenu(true);
                      }}
                      className='xl:hidden'
                    />
                  </div>}
                </div>
              </div>
            </div>
          </div>
        </Grid2>
        <GlobalBannerContainer />
      </div>
      <Suspense>
        {/*
          This component has a suspense boundary issue, which would cause the whole page to rerender after ssr.
          Wrapping it in it's own suspense prevents this.
        */}
        <SideMenu show={showSideMenu} toggle={() => (setShowSideMenu((x) => !x))} />
        <Modal
          title={'Disconnected'}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
          }}
          isDismissable
          showCloseButton
          aria-labelledby={'disconnection-modal'}
          footerButtons={<>
            <Button onPress={() => setShowModal(false)}>
              Close
            </Button>
          </>}
        >
          <Banner color='error' size='sm'>
            <div className='flex flex-col gap-2'>
              <div>Unable to connect to live update server, attempting reconnection. If this persists, please try refreshing
                your browser.</div>
              <div>Disconnected state indicated by <IconWarning/> at top of page. If you cannot see this icon, you are connected.</div>
            </div>
          </Banner>
        </Modal>
      </Suspense>
      <Button unstyled className='absolute' id='maincontent' aria-label='Main content' />
    </>
  );
};
